/**
 * RequireModule — route guard for pages owned by a per-account module
 * (Prospera-Corp-App Module Settings, read via `GET /account/modules`).
 *
 * Wrap routes in a pathless <Route element={<RequireModule moduleKey="…" />}>;
 * module off → redirect to the safe landing path (the first nav item the
 * account can actually see — NOT hardcoded `/`, which is itself module-gated
 * for restricted accounts). Fail-open while loading, matching useModuleAccess
 * semantics — staff are never flashed out of a page they may have.
 * Section-level gating lives in per-module layouts (see MarketingLayout). Admin
 * pages follow their per-account level instead (RequireAdminPage).
 *
 * `requireActive`: fail-CLOSED variant for opt-in pages (e.g. the Facilities
 * admin page) — the module must have an explicitly ACTIVE row, mirroring the
 * nav's `requiresModuleActive`.
 *
 * `notFound`: render the 404 page instead of redirecting. Use when the surface
 * should look like it does not exist for accounts without the module (e.g.
 * /marketing/social without Zernio) rather than silently bouncing the user to
 * their landing page.
 *
 * `holdOnceAllowed` (with `requireActive`): check on entry only. Once a fresh
 * fetch has let the page open, later refetches — which run on window focus with
 * no retry, so one network blip reads as "off" — do not take it away. For pages
 * that run long, un-abortable work (the lead import), where a mid-run redirect
 * would lose the result; the server gate still refuses further requests if the
 * module really was switched off. The hold lives in this component's state and
 * every branch returns the same <Outlet />, so the page is never remounted.
 * Same idea as `useDesktopGate` in RequireDesktop.tsx.
 */
import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { isModulesFresh, useAccountModules, useModuleAccess } from '@/hooks/useAccountModules';
import { useSafeLandingPath } from '@/hooks/useNavAccess';
import { NotFoundPage } from '@/pages/NotFoundPage';

export default function RequireModule({
  moduleKey,
  requireActive = false,
  notFound = false,
  holdOnceAllowed = false,
}: {
  moduleKey: string;
  requireActive?: boolean;
  notFound?: boolean;
  holdOnceAllowed?: boolean;
}) {
  const { enabled, explicit, isLoading } = useModuleAccess(moduleKey);
  const query = useAccountModules();
  const landing = useSafeLandingPath();
  const [held, setHeld] = useState(false);
  const blocked = notFound ? <NotFoundPage /> : <Navigate to={landing} replace />;

  if (requireActive && holdOnceAllowed) {
    if (held) return <Outlet />;
    if (!isLoading && explicit) {
      // Setting state during render is React's pattern for remembering a
      // value from an earlier render; it re-renders once and then holds.
      setHeld(true);
      return <Outlet />;
    }
  }

  if (requireActive) {
    // Opt-in gate: block unless a FRESH fetch reported an explicitly active
    // row (`explicit` from useModuleAccess is freshness-gated). While loading
    // we render nothing (not a redirect) to avoid bouncing a legitimately-
    // enabled account on a slow first fetch — including the seed-only state,
    // where data exists but no real fetch has landed yet and the background
    // refetch is still in flight. Once the query settles without a fresh
    // success (fetch errored), redirecting is the correct fail-closed outcome.
    if (isLoading) return null;
    if (!explicit) {
      if (!isModulesFresh(query) && query.isFetching) return null;
      return blocked;
    }
    return <Outlet />;
  }

  if (!isLoading && !enabled) {
    return blocked;
  }
  return <Outlet />;
}
